/* Desktop menus are native <details data-menu>: tap and keyboard work without
   JS. This file adds: one open menu at a time, outside click / Esc / focus-out
   closing, hover opening on devices that hover, the panel morph between items
   (measured from the approved recording: the panel resizes and slides to the
   next item while its content fades), viewport clamping, and the hover-swapped
   preview image. Durations come from the tokens (--duration-menu-out). */
const menus = () => document.querySelectorAll('details[data-menu]');
const panel = (m) => m.querySelector('[data-mega-panel]');
const canHover = window.matchMedia('(hover: hover)').matches;
const motion = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const cssMs = (name, fallback) => parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || fallback;
const cssPx = (name, fallback) => parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || fallback;

let lastBox = null;
let lastAt = 0;
let hoverTimer = null;
const closeTimers = new WeakMap();

/* Keep the panel inside the viewport (it is anchored to its menu item). */
function place(m) {
  const p = panel(m);
  if (!p) return;
  p.style.removeProperty('--mega-shift');
  const gutter = cssPx('--space-4', 16);
  const r = p.getBoundingClientRect();
  const over = r.right - (window.innerWidth - gutter);
  if (over > 0) p.style.setProperty('--mega-shift', `${-Math.min(over, Math.max(0, r.left - gutter))}px`);
}

function remember(m) {
  const p = panel(m);
  if (p) { lastBox = p.getBoundingClientRect(); lastAt = performance.now(); }
}

function closeMenu(m, animate) {
  clearTimeout(closeTimers.get(m));
  if (!m.open) return;
  remember(m);
  if (animate && panel(m) && motion()) {
    m.setAttribute('data-closing', '');
    closeTimers.set(m, setTimeout(() => { m.removeAttribute('data-closing'); m.open = false; }, cssMs('--duration-menu-out', 160)));
  } else {
    m.removeAttribute('data-closing');
    m.open = false;
  }
}

function morph(p, from) {
  const to = p.getBoundingClientRect();
  p.classList.add('mega--morph');
  p.style.width = `${from.width}px`;
  p.style.height = `${from.height}px`;
  p.style.translate = `${from.left - to.left}px ${from.top - to.top}px`;
  p.getBoundingClientRect(); // commit the start box before transitioning
  p.style.width = `${to.width}px`;
  p.style.height = `${to.height}px`;
  p.style.translate = '0 0';
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    p.classList.remove('mega--morph');
    p.style.width = p.style.height = p.style.translate = '';
  };
  p.addEventListener('transitionend', finish, { once: true });
  setTimeout(finish, cssMs('--duration-menu', 260) + 100);
}

function openMenu(m) {
  clearTimeout(closeTimers.get(m));
  if (m.open && !m.hasAttribute('data-closing')) return;
  let from = null;
  menus().forEach((o) => {
    if (o === m || !o.open) return;
    remember(o);
    closeMenu(o, false);
  });
  if (lastBox && performance.now() - lastAt < cssMs('--duration-menu', 260) + 120) from = lastBox;
  m.removeAttribute('data-closing');
  m.open = true;
  const p = panel(m);
  if (!p) return;
  place(m);
  if (from && motion()) morph(p, from);
}

/* Native toggles (tap, keyboard): one open at a time, keep in viewport. */
document.addEventListener('toggle', (e) => {
  const el = e.target;
  if (!(el instanceof HTMLDetailsElement) || !el.hasAttribute('data-menu') || !el.open) return;
  menus().forEach((m) => m !== el && closeMenu(m, false));
  place(el);
}, true);

document.addEventListener('click', (e) => {
  menus().forEach((m) => { if (m.open && !m.contains(e.target)) closeMenu(m, true); });
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  menus().forEach((m) => {
    if (m.open) { closeMenu(m, false); m.querySelector('summary')?.focus(); }
  });
});

/* Keyboard users: leaving a menu with Tab closes it. */
document.addEventListener('focusout', (e) => {
  const m = e.target.closest('details[data-menu]');
  if (m && m.open && !(e.relatedTarget && m.contains(e.relatedTarget))) closeMenu(m, false);
});

window.addEventListener('resize', () => menus().forEach((m) => closeMenu(m, false)));

/* Hover: open desktop mega menus on pointer hover (tap/keyboard still work). */
if (canHover) {
  document.addEventListener('mouseover', (e) => {
    const item = e.target.closest('details[data-menu].menu-item--mega');
    if (!item) return;
    clearTimeout(hoverTimer);
    openMenu(item);
  });
  document.addEventListener('mouseout', (e) => {
    const item = e.target.closest('details[data-menu].menu-item--mega');
    if (!item || (e.relatedTarget && item.contains(e.relatedTarget))) return;
    hoverTimer = setTimeout(() => closeMenu(item, true), 150);
  });
}

/* Preview style: hovering or focusing a link swaps the image on the right. */
function swapPreview(e) {
  const link = e.target.closest('[data-preview]');
  if (!link) return;
  const p = link.closest('[data-mega-panel]');
  const stage = p?.querySelector('[data-preview-stage]');
  if (!stage) return;
  const img = stage.querySelector(`[data-preview-img="${link.dataset.preview}"]`);
  if (!img) return;
  stage.querySelectorAll('.is-active').forEach((i) => i.classList.remove('is-active'));
  img.classList.add('is-active');
  p.querySelectorAll('[data-preview].is-active').forEach((i) => i.classList.remove('is-active'));
  link.classList.add('is-active');
}
document.addEventListener('mouseover', swapPreview);
document.addEventListener('focusin', swapPreview);
